import React from "react";
import { Box, Tooltip } from "@mui/material";
import { MRT_ColumnDef } from "material-react-table";

import { Label12Regular } from "components";
import { EMPTY_CELL_VALUE } from "consts";

import { isNullOrUndefined } from "utils";
import { TableRow } from "../types";
import {
  createHeaderWithEllipsis,
  createNumberCell,
  createUnitCell,
  createAggregatedMeasureValueCell,
  createRenewableUsageCell,
  createEmissionCell,
  createIntensityCell,
  headerStyles,
  cellStyles,
  aggregatedCellStyles,
  aggregatedValueStyles,
  aggregatedUnitStyles
} from "../helpers";

export const SITE_EMISSIONS_COLUMNS_MAIN: MRT_ColumnDef<TableRow>[] = [
  {
    id: 'Properties',
    header: 'Properties',
    muiTableHeadCellProps: { sx: headerStyles },
    columns: [
      createNumberCell("startDate", "Start Date"),
      createNumberCell("endDate", "End Date"),
      createNumberCell("daysCovered", "Number of Days"),
      {
        Header: createHeaderWithEllipsis("Number of Unique Account/Meters"),
        header: "Number of Unique Account/Meters",
        accessorKey: "numberOfUniqueCollectors",
        Cell: ({ cell }: { cell: any }) => {
          const value = cell.getValue();
          if (isNullOrUndefined(value)) {
            return EMPTY_CELL_VALUE;
          }
          return <Box>{(value as number)}</Box>;
        },
      },
      createNumberCell("country", "Country"),
      createNumberCell("sitetype", "Type of Site"),
      {
        Header: createHeaderWithEllipsis("Business Unit"),
        accessorKey: "businessUnit",
        header: "Business Unit",
        Cell: ({ cell }: { cell: any }) => {
          const value = cell.getValue() as string;
          if (!value) {
            return <Box sx={{ paddingLeft: "15px" }}>{EMPTY_CELL_VALUE}</Box>;
          }
          return (
            <Tooltip title={value}>
              <Box>{value}</Box>
            </Tooltip>
          );
        },
      },
      {
        Header: createHeaderWithEllipsis("Customer Site ID"),
        header: "Customer Site ID",
        accessorKey: "customerSiteId",
        Cell: ({ cell }: { cell: any }) => {
          const value = cell.getValue() as string;
          if (isNullOrUndefined(value)) {
            return <Box sx={{ mx: "auto" }}>{EMPTY_CELL_VALUE}</Box>;
          }
          return (
            <Box
              sx={{
                alignItems: "center",
                textOverflow: "ellipsis",
                whiteSpace: "normal",
                fontSize: "14px",
                fontWeight: 400,
                fontFamily: "Manrope",
              }}
            >
              {value}
            </Box>
          );
        },
      },
      createNumberCell("sitename", "Site"),
      createNumberCell("utilityName", "Utility Type"),
      {
        Header: createHeaderWithEllipsis("Estimation Methodology"),
        header: "Estimation Methodology",
        accessorKey: "estimationMethodology",
        Cell: ({ cell }: { cell: any }) => {
          const value = cell.getValue();
          if (isNullOrUndefined(value)) {
            return EMPTY_CELL_VALUE;
          }
          return (
            <Tooltip title={value}>
              <Box>{(value as number)}</Box>
            </Tooltip>
          );
        },
        size: 200,
      },
      createNumberCell("scope", "Scope"),
      createNumberCell("source", "Source"),
    ]
  },
  {
    id: 'Usage',
    header: 'Usage',
    muiTableHeadCellProps: { sx: headerStyles },
    columns: [
      {
        Header: createHeaderWithEllipsis("Usage"),
        header: "Usage",
        accessorKey: "usage",
        enableGrouping: false,
        Cell: ({ cell, row }: { cell: any; row: any }) => {
          const value = cell.getValue();
          if (isNullOrUndefined(value)) {
            return EMPTY_CELL_VALUE;
          }
          const { unit } = row.original;
          return (
            <Box sx={cellStyles}>
              <Box>{(Number(value)?.toFixed(2))}</Box>
              <Label12Regular sx={{ ml: "5px", mt: "2px" }}>{unit}</Label12Regular>
            </Box>
          );
        },
        AggregatedCell: ({ cell, row }: { cell: any; row: any }) => {
          const value = cell.getValue();
          const { unit } = row.original;
          return (
            <>
              {cell.row.groupingColumnId === 'utilityName' && (
                <Box sx={aggregatedCellStyles}>
                  <Box sx={aggregatedValueStyles}>{(value as number).toFixed(4)}</Box>
                  <Label12Regular sx={aggregatedUnitStyles}>{unit}</Label12Regular>
                </Box>
              )}
            </>
          );
        },
        aggregationFn: 'sum',
      },
      {
        Header: createHeaderWithEllipsis("Standardized Usage (kWh)"),
        header: "Standardized Usage (kWh)",
        accessorFn: (row) => row.standardizedUsageKWh?.measureValue,
        enableGrouping: false,
        aggregationFn: 'sum',
        Cell: ({ row }: { row: any }) => {
          const value = row.original.standardizedUsageKWh;
          if (isNullOrUndefined(value?.measureValue) || isNullOrUndefined(value?.measureUnit)) {
            return EMPTY_CELL_VALUE;
          }
          return <Box>{`${(Number(value.measureValue)?.toFixed(2))} ${value.measureUnit}`}</Box>;
        },
        AggregatedCell: ({ cell }: { cell: any }) => {
          const value = cell.getValue();
          return createAggregatedMeasureValueCell(value, "kWh");
        }
      },
      {
        Header: createHeaderWithEllipsis("Standardized Usage (ML)"),
        header: "Standardized Usage (ML)",
        accessorFn: (row) => row.standardizedUsageML?.measureValue,
        enableGrouping: false,
        aggregationFn: 'sum',
        Cell: ({ row }: { row: any }) => {
          const value = row.original.standardizedUsageML;
          if (isNullOrUndefined(value?.measureValue) || isNullOrUndefined(value?.measureUnit)) {
            return EMPTY_CELL_VALUE;
          }
          return <Box>{`${(Number(value.measureValue)?.toFixed(2))} ${value.measureUnit}`}</Box>;
        },
        AggregatedCell: ({ cell }: { cell: any }) => {
          const value = cell.getValue();
          return createAggregatedMeasureValueCell(value, "ML");
        }
      },
      {
        Header: createHeaderWithEllipsis("Standardized Usage (T)"),
        header: "Standardized Usage (T)",
        accessorFn: (row) => row.standardizedUsageT?.measureValue,
        enableGrouping: false,
        aggregationFn: 'sum',
        Cell: ({ row }: { row: any }) => {
          const value = row.original.standardizedUsageT;
          if (isNullOrUndefined(value?.measureValue) || isNullOrUndefined(value?.measureUnit)) {
            return EMPTY_CELL_VALUE;
          }
          return <Box>{`${(Number(value.measureValue)?.toFixed(2))} ${value.measureUnit}`}</Box>;
        },
        AggregatedCell: ({ cell }: { cell: any }) => {
          const value = cell.getValue();
          return createAggregatedMeasureValueCell(value, "T");
        }
      },
    ]
  },
  {
    id: 'Cost',
    header: 'Cost',
    muiTableHeadCellProps: { sx: headerStyles },
    columns: [
      createUnitCell("cost", "Cost", "currency", "sum", 100),
    ]
  },
];

export const locationBasedColumns: MRT_ColumnDef<TableRow>[] = [
  {
    id: 'Location',
    header: 'Location',
    muiTableHeadCellProps: { sx: headerStyles },
    columns: [
      createRenewableUsageCell("locationRenewableUsage", "Renewable Usage") as MRT_ColumnDef<TableRow>,
      createRenewableUsageCell("locationNonRenewableUsage", "Non-Renewable Usage") as MRT_ColumnDef<TableRow>,
    ]
  },
  {
    id: 'Emissions',
    header: 'Emissions - Location',
    muiTableHeadCellProps: { sx: headerStyles },
    columns: [
      createEmissionCell("co2Emission", "Emissions", 140),
      createEmissionCell("co2OnlyEmission", "CO2"),
      createEmissionCell("ch4OnlyEmission", "CH4 Fossil"),
      createEmissionCell("ch4NonFossilEmission", "CH4 Non-Fossil"),
      createEmissionCell("n2oOnlyEmission", "N2O"),
    ]
  },
];

export const marketColumns: MRT_ColumnDef<TableRow>[] = [
  {
    id: 'Market',
    header: 'Market',
    muiTableHeadCellProps: { sx: headerStyles },
    columns: [
      createRenewableUsageCell("marketRenewableUsage", "Renewable Usage") as MRT_ColumnDef<TableRow>,
      createRenewableUsageCell("marketNonRenewableUsage", "Non-Renewable Usage") as MRT_ColumnDef<TableRow>,
    ]
  },
  {
    id: 'Emissions - Market',
    header: 'Emissions - Market',
    muiTableHeadCellProps: { sx: headerStyles },
    columns: [
      createEmissionCell("marketCo2Emission", "Emissions - Market"),
      createEmissionCell("marketCo2OnlyEmission", "CO2 - Market"),
      createEmissionCell("marketCh4OnlyEmission", "CH4 - Market"),
      createEmissionCell("marketCh4NonFossilEmission", "CH4 Non-Fossil - Market"),
      createEmissionCell("marketN2oOnlyEmission", "N2O - Market"),
    ]
  },
];

export const intensityColumns: MRT_ColumnDef<TableRow>[] = [
  {
    id: 'Area',
    header: 'Area',
    muiTableHeadCellProps: { sx: headerStyles },
    columns: [
      createUnitCell("area", "Area", "intensityAreaUnit", "sum"),
      createUnitCell("revenue", "Revenue", "intensityRevenueUnit", "sum"),
    ]
  },
  {
    id: 'Production',
    header: 'Production',
    muiTableHeadCellProps: { sx: headerStyles },
    columns: [
      createUnitCell("productUnit", "Production", "unitsForIntensityProduct", "sum"),
    ]
  },
  {
    id: 'Emissions Intensity',
    header: 'Emissions Intensity',
    muiTableHeadCellProps: { sx: headerStyles },
    columns: [
      createIntensityCell("emissionByIntensityArea", "By Area", "intensityAreaUnit"),
      createIntensityCell("emissionByIntensityProductUnit", "By Production", "unitsForIntensityProduct"),
      createIntensityCell("emissionByIntensityRevenue", "By Revenue", "intensityRevenueUnit"),
      createIntensityCell("marketEmissionByIntensityArea", "By Area - Market", "intensityAreaUnit"),
      createIntensityCell("marketEmissionByIntensityProductUnit", "By Production - Market", "unitsForIntensityProduct"),
      createIntensityCell("marketEmissionByIntensityRevenue", "By Revenue - Market", "intensityRevenueUnit"),
    ]
  },
];
